import {
    createCallRouting,
    getPredefs,
} from 'flow/actions/call-routing-actions';
import { fetchProfiles } from 'flow/actions/profile-actions';
import Button from 'flow/components//ui-components/Button';
import ReferralNumberCombobox, {
    ReferralNumberOption,
} from 'flow/components/Setting/components/ReferralNumberCombobox';
import SettingRow from 'flow/components/Setting/SettingRow';
import SettingSection from 'flow/components/Setting/SettingSection';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Divider from 'flow/components/ui-components/Divider';
import DropdownMenuComponent, {
    OptionProps,
} from 'flow/components/ui-components/DropdownMenuComponent';
import InputField from 'flow/components/ui-components/InputField';
import RadioButton from 'flow/components/ui-components/RadioButton';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { Blocklist, BlocklistPredefDto } from 'flow/types/Callrouting';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';

import { TypedMap } from 'flow/types/TypedMap';
import { numberIsValidMatcher } from 'flow/utils/call-utils';
import { fromJS, List } from 'immutable';
import React, { ChangeEvent, useEffect, useId, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

type BlockListType = 'blocked_in' | 'blocked_out' | 'priority';
type PredefListType = 'predefList_in' | 'predefList_out';

interface ExceptionsDTO {
    matcher: string[];
}

const formatWithPredefList = (type: BlockListType): PredefListType => {
    // Takes the input and trims everything before and including '_'
    return ('predefList_' + type.replace(/^.*?_/, '')) as PredefListType;
};

const isValueAlreadyExists = (
    blocklists: TypedMap<Blocklist>,
    newBlocklistMatcher: string,
) => {
    return blocklists.some((blocklist) => {
        const matcher = blocklist?.get('matcher') as string;
        return matcher.replace(/\*$/, '') === newBlocklistMatcher;
    });
};

interface Props {
    extensionKey?: ExtensionKey;
    type?: BlockListType;
}

const CreateProfileModal = ({ extensionKey, type }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { profiles } = useProfiles(extensionKey);

    const predefList = useAppSelector((state) =>
        state.callRoutings.get(formatWithPredefList(type)),
    );

    const callRoutings = useAppSelector((state) =>
        state.callRoutings.getIn([type]),
    );

    const [redirect, setRedirect] = useState(null);
    const [predef, setPredef] = useState<TypedMap<BlocklistPredefDto> | string>(
        '',
    );
    const [number, setNumber] = useState('');
    const [exceptionInput, setExceptionInput] = useState('');
    const [exceptions, setExceptions] = useState<ExceptionsDTO>(null);
    const [profileDuring, setProfileDuring] = useState<ProfileKey>(null);
    const [referralNumber, setReferralNumber] =
        useState<ReferralNumberOption>();
    const [numberInputError, setNumberInputError] = useState('');
    const [error, setError] = useState('');

    // Ties each hint and error paragraph to its field with aria-describedby.
    const idPrefix = useId();
    const numberErrorId = `${idPrefix}-number-error`;
    const exceptionAddHintId = `${idPrefix}-exception-add-hint`;
    const exceptionHelpId = `${idPrefix}-exception-help`;
    const exceptionErrorId = `${idPrefix}-exception-error`;
    const exceptionDescribedBy = [
        type !== 'blocked_out' ? exceptionAddHintId : null,
        exceptionHelpId,
        error ? exceptionErrorId : null,
    ]
        .filter(Boolean)
        .join(' ');

    const [
        shouldShowForwardToNumberRedirect,
        setShouldShowForwardToNumberRedirect,
    ] = useState(false);
    const [
        shouldShowForwardToNumberAction,
        setShouldShowForwardToNumberAction,
    ] = useState(false);

    useEffect(() => {
        dispatch(getPredefs());
        dispatch(fetchProfiles(extensionKey));
    }, []);

    const profileOptions = profiles
        .map((profile) => ({
            key: profile.get('key'),
            label: profile.get('description'),
        }))
        .toArray() as OptionProps[];

    const options: OptionProps[] = [
        {
            key: null,
            label: i18next.t('settings.callrouting.profile.allprofiles'),
        },
    ].concat(profileOptions);

    let blockListDTO = fromJS({
        editByUser: true,
        isIncoming: type === 'priority' || type === 'blocked_in',
        redirect: redirect,
    });

    const createNewCallRouting = () => {
        if (exceptions) {
            const matcherObjects = exceptions.matcher.map((value) => ({
                matcher: value,
            }));
            blockListDTO = blockListDTO.setIn(
                ['blocklistExceptions'],
                List(matcherObjects),
            );
        }

        if (profileDuring) {
            blockListDTO = blockListDTO.setIn(
                ['profileDTO'],
                profiles.find(
                    (profile) => profile.get('key') === profileDuring,
                ),
            );
        }

        if (numberIsValidMatcher(number)) {
            blockListDTO = blockListDTO.setIn(['matcher'], number);
        }

        if (redirect || referralNumber) {
            blockListDTO = blockListDTO.setIn(
                ['redirect'],
                referralNumber?.dto.get('number'),
            );
        } else {
            blockListDTO = blockListDTO.setIn(['redirect'], redirect);
        }

        if (predef) {
            blockListDTO = blockListDTO.setIn(['blocklistPredefDTO'], predef);
        }

        if (type === 'priority') {
            blockListDTO = blockListDTO.setIn(['redirect'], 'passthrough');
        }

        dispatch(createCallRouting(blockListDTO));
    };

    const handleNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newNumber = e.target.value;
        setNumber(newNumber);

        if (isValueAlreadyExists(callRoutings, newNumber)) {
            setNumberInputError('Number already exists');
        } else {
            setNumberInputError('');
        }
    };

    const handleAddException = (exception: string) => {
        setExceptions((prevDTO) => {
            if (prevDTO?.matcher.includes(exception)) {
                setError(`Exception for ${exception} already exists`);
                return prevDTO;
            } else {
                setError('');
                return {
                    ...prevDTO,
                    matcher: [...(prevDTO?.matcher || []), exception],
                };
            }
        });
    };

    const handleRemoveException = (indexToRemove: number) => {
        setExceptions((prevDTO) => ({
            ...prevDTO,
            matcher: prevDTO.matcher.filter(
                (_, index) => index !== indexToRemove,
            ),
        }));
    };

    const findLabelByKey = (key: ProfileKey) => {
        const option = options.find((option) => option.key === key);
        return option ? option.label : '';
    };

    const isDisabled = () => {
        if (predef === '') {
            return !(
                exceptionInput.startsWith(number) &&
                exceptionInput.length > number.length
            );
        }
        return false;
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('settings.callrouting.add.redirect')}
            disableSubmit={!(numberIsValidMatcher(number) || predef)}
            onSubmit={createNewCallRouting}
            confirmButtonText={i18next.t('settings.callrouting.add.redirect')}
            className="w-[600px]"
        >
            <div className="flex flex-col px-2 pb-9">
                <SettingSection>
                    {i18next.t(
                        'settings.callrouting.add.option.number.redirect',
                    )}
                </SettingSection>

                {type !== 'priority' ? (
                    <RadioButton
                        name={'number'}
                        onChange={() => {
                            setShouldShowForwardToNumberRedirect(true);
                            setPredef('');
                        }}
                        checked={
                            predef === '' && shouldShowForwardToNumberRedirect
                        }
                    >
                        {i18next.t('settings.number')}
                    </RadioButton>
                ) : null}

                {predefList &&
                    predefList
                        .sort()
                        .map((predefOption: TypedMap<BlocklistPredefDto>) => {
                            return (
                                <RadioButton
                                    key={predefOption?.get('name')}
                                    name={predefOption?.get('name')}
                                    onChange={() => {
                                        setPredef(predefOption);
                                        setRedirect(null);
                                    }}
                                    checked={predef === predefOption}
                                >
                                    {predefOption?.get('description')}
                                </RadioButton>
                            );
                        })}

                {(shouldShowForwardToNumberRedirect && !predef) ||
                type === 'priority' ? (
                    <>
                        <SettingSection>
                            {i18next.t('settings.number')}
                            <span>&nbsp;*</span>
                        </SettingSection>
                        <InputField
                            placeholder={'Enter a number'}
                            value={number}
                            onChange={handleNumberChange}
                            aria-invalid={numberInputError ? true : undefined}
                            aria-describedby={
                                numberInputError ? numberErrorId : undefined
                            }
                        />
                        {numberInputError && (
                            <p
                                id={numberErrorId}
                                role="alert"
                                className="text-red pr-2 text-sm"
                            >
                                {numberInputError}
                            </p>
                        )}
                    </>
                ) : null}
                <Divider />

                {type === 'blocked_in' ? (
                    <>
                        <SettingSection>
                            {i18next.t('settings.callrouting.action')}
                        </SettingSection>

                        <RadioButton
                            name={'settings.callrouting.action.playblock'}
                            onChange={() => {
                                setShouldShowForwardToNumberAction(false);
                                setRedirect(null);
                            }}
                            checked={
                                !redirect && !shouldShowForwardToNumberAction
                            }
                        >
                            {i18next.t('settings.callrouting.action.playblock')}
                        </RadioButton>

                        <RadioButton
                            name={'settings.callrouting.action.tovoicemail'}
                            onChange={() => {
                                setShouldShowForwardToNumberAction(false);
                                setRedirect('voicemail');
                            }}
                            checked={redirect === 'voicemail'}
                        >
                            {i18next.t(
                                'settings.callrouting.action.tovoicemail',
                            )}
                        </RadioButton>

                        <RadioButton
                            name={'settings.callrouting.action.tonumber'}
                            onChange={() => {
                                setShouldShowForwardToNumberAction(true);
                                setRedirect(null);
                            }}
                            checked={shouldShowForwardToNumberAction}
                        >
                            {i18next.t('settings.callrouting.action.tonumber')}
                        </RadioButton>

                        <Divider />
                    </>
                ) : null}

                {shouldShowForwardToNumberAction && !redirect ? (
                    <>
                        <SettingSection className="text-sm/4">
                            {i18next.t(
                                'settings.callrouting.action.picknumber',
                            )}
                            <span>&nbsp;*</span>
                        </SettingSection>
                        <ReferralNumberCombobox
                            ariaLabel={i18next.t(
                                'settings.callrouting.action.picknumber',
                            )}
                            referralNumber={referralNumber}
                            onChange={setReferralNumber}
                        />
                    </>
                ) : null}

                <SettingSection>
                    {i18next.t('settings.callrouting.exceptions')}
                </SettingSection>

                <SettingSection>{i18next.t('settings.number')}</SettingSection>

                <InputField
                    placeholder={'Enter a number'} // Order translation
                    value={exceptionInput}
                    disabled={!number && redirect}
                    defaultValue={number}
                    onChange={(e) => {
                        setExceptionInput(e.target.value);
                        if (error) setError('');
                    }}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={exceptionDescribedBy}
                />

                {type !== 'blocked_out' ? (
                    <p
                        id={exceptionAddHintId}
                        className="p-2 text-xs text-gray-500"
                    >
                        {i18next.t('settings.callrouting.exceptions.add.error')}
                    </p>
                ) : null}

                <p id={exceptionHelpId} className="p-2 text-xs text-gray-500">
                    {i18next.t('settings.callrouting.exceptions.helptext')}
                </p>

                <Button
                    color="primary"
                    className="my-4 min-w-max px-5"
                    disabled={isDisabled()}
                    onClick={() => {
                        if (exceptionInput.trim() !== '') {
                            handleAddException(exceptionInput.trim());
                            setExceptionInput('');
                        }
                    }}
                >
                    {i18next.t('settings.callrouting.exceptions.add')}
                </Button>

                {error && (
                    <p
                        id={exceptionErrorId}
                        role="alert"
                        className="text-red pr-2 text-sm"
                    >
                        {error}
                    </p>
                )}

                {exceptions &&
                    exceptions.matcher.map(
                        (exception: string, index: number) => (
                            <SettingRow
                                key={exception}
                                cta={
                                    <Button
                                        iconKey="delete"
                                        onClick={() =>
                                            handleRemoveException(index)
                                        }
                                    />
                                }
                            >
                                {exception}
                            </SettingRow>
                        ),
                    )}

                <Divider />

                <SettingSection>
                    {i18next.t('settings.callrouting.during')}
                </SettingSection>

                <DropdownMenuComponent
                    options={options}
                    title={findLabelByKey(profileDuring)}
                    onItemSelected={(profile: ProfileKey) =>
                        setProfileDuring(profile)
                    }
                />

                <Divider />

                <span className="text-sm/4">
                    *&nbsp;
                    {i18next.t('fields.required_field')}
                </span>
            </div>
        </BaseModal>
    );
};

export default CreateProfileModal;
